import React, { useEffect } from 'react';
import { Field, useFormikContext } from 'formik';

import {
  InputUI,
  FormErrorUI,
  FormLabelUI,
  DatePickerUI,
  TimePickerUi,
  SelectUI,
  AutoCompleteSelectUI,
} from '../Interface';
import {
  MonthDaysList,
  TaskScheduleType,
  TaskScheduleTypeList,
  UnitEnum,
  UnitList,
  UnitsWithFrequency,
  WeekDaysList,
} from '../../config/taskSchedule';

export const TaskScheduleFields = ({ viewField, autocompleteOptions, selectedValues }) => {
  const { values, errors, validateField, setFieldValue } = useFormikContext();

  useEffect(() => {
    const unit = values.unit;

    if (unit !== UnitEnum.WEEK && values.weekday != null) {
      setFieldValue('weekday', null);
    }

    if (unit !== UnitEnum.MONTH && values.monthDay != null) {
      setFieldValue('monthDay', null);
    }

    if (unit === UnitEnum.WEEK && (values.weekday === null || values.weekday === undefined || values.weekday === '')) {
      setFieldValue('weekday', 1);
    }

    if (
      unit === UnitEnum.MONTH &&
      (values.monthDay === null || values.monthDay === undefined || values.monthDay === '')
    ) {
      setFieldValue('monthDay', 1);
    }
  }, [values.unit]);

  return (
    <div className="grid grid-cols-4 gap-x-4 mx-4 mb-5">
      <div className="col-span-4">
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Megnevezés" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          disabled={viewField}
          InputLabelProps={{ shrink: values.name && values.name !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="startDate"
          component={DatePickerUI}
          value={values.startDate}
          fullWidth
          label={<FormLabelUI text="Kezdő dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.startDate} />}
          onChange={(date) => {
            setFieldValue('startDate', date).then(() => {
              validateField('startDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.startDate !== '' }}
        />
      </div>
      <div className="col-span-4 md:col-span-1">
        <Field
          type="date"
          name="endDate"
          component={DatePickerUI}
          value={values.endDate}
          fullWidth
          label={<FormLabelUI text="Befejező dátum" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.endDate} />}
          onChange={(date) => {
            setFieldValue('endDate', date).then(() => {
              validateField('endDate');
            });
          }}
          disabled={viewField}
          InputLabelProps={{ shrink: values.endDate !== '' }}
        />
      </div>
      {values.type === TaskScheduleType.SIMPLETASK && (
        <>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="time"
              name="fromTime"
              component={TimePickerUi}
              value={values.fromTime}
              fullWidth
              label={<FormLabelUI text="Kezdő időpont" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.fromTime} />}
              onChange={(date) => {
                setFieldValue('fromTime', date).then(() => {
                  validateField('fromTime');
                });
              }}
              InputLabelProps={{ shrink: values.fromTime !== '' }}
              disabled={viewField}
            />
          </div>
          <div className="col-span-4 md:col-span-1">
            <Field
              type="time"
              name="toTime"
              component={TimePickerUi}
              value={values.toTime}
              fullWidth
              label={<FormLabelUI text="Befejező időpont" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.toTime} />}
              onChange={(date) => {
                setFieldValue('toTime', date).then(() => {
                  validateField('toTime');
                });
              }}
              InputLabelProps={{ shrink: values.toTime !== '' }}
              disabled={viewField}
            />
          </div>
        </>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          name="type"
          component={SelectUI}
          fullWidth
          option={TaskScheduleTypeList}
          value={values.type ?? ''}
          label={<FormLabelUI text="Típus" />}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;

            setFieldValue('type', value ?? null).then(() => {
              validateField('type');
            });

            setFieldValue('projectId', null);
          }}
          helperText={<FormErrorUI message={errors.type} />}
          disabled={viewField}
        />
      </div>
      {values.type === TaskScheduleType.TASKLIST && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="projectId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Projekt" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.projectId} />}
            onChange={(_e, newVal) => {
              setFieldValue('projectId', newVal?.value ?? null).then(() => {
                validateField('projectId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.projectId !== '' }}
            selectedValue={values.projectId}
            selectedLabelValue={selectedValues?.project?.projectName}
            dataset={autocompleteOptions.project}
          />
        </div>
      )}
      {UnitsWithFrequency.includes(values.unit) && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="number"
            name="every"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Ismétlődés gyakorisága" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.every} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.every && values.every !== '' }}
          />
        </div>
      )}
      <div className="col-span-4 md:col-span-1">
        <Field
          name="unit"
          component={SelectUI}
          fullWidth
          option={UnitList}
          value={values.unit ?? ''}
          label={<FormLabelUI text="Rendszeresség" />}
          variant="standard"
          onChange={(e) => {
            const value = e.target.value;

            if (UnitsWithFrequency.includes(value)) {
              setFieldValue('every', 1);
            } else {
              setFieldValue('every', '');
            }

            setFieldValue('unit', value ?? null).then(() => {
              validateField('unit');
            });
          }}
          helperText={<FormErrorUI message={errors.unit} />}
          disabled={viewField}
        />
      </div>
      {values.unit === UnitEnum.WEEK && (
        <div className="col-span-4 md:col-span-1">
          <Field
            name="weekday"
            component={SelectUI}
            fullWidth
            option={WeekDaysList}
            label={<FormLabelUI text="Hét napja" />}
            value={values.weekday ?? ''}
            variant="standard"
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('weekday', value ?? null).then(() => {
                validateField('weekday');
              });
            }}
            helperText={<FormErrorUI message={errors.weekday} />}
            disabled={viewField}
          />
        </div>
      )}
      {values.unit === UnitEnum.MONTH && (
        <div className="col-span-4 md:col-span-1">
          <Field
            type="number"
            name="monthDay"
            as={SelectUI}
            label={<FormLabelUI text="Hónap napja" />}
            fullWidth
            option={MonthDaysList}
            variant="standard"
            value={values.monthDay ?? ''}
            onChange={(e) => {
              const value = e.target.value;
              setFieldValue('monthDay', value ?? null).then(() => {
                validateField('monthDay');
              });
            }}
            helperText={<FormErrorUI message={errors.monthDay} />}
            disabled={viewField}
          />
        </div>
      )}
    </div>
  );
};

export default TaskScheduleFields;
